import { getVariantDisplayQuantity } from '../../../utils/variant-display';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AdminPaginatorComponent } from '../../../Design/admin-paginator.component';
import { StockReasonComponent } from '../../../Design/stock-reason.component';
import { IndianCompactPipe } from '../../../pipes/indian-compact.pipe';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { StoreProductService } from '../../../services/store-product/store-product.service';
import { AuthService } from '../../../services/auth/auth.service';
import { getPublicUrl } from '../../../core/supabase/supabase.client';
import {
  deriveInventoryStatus,
  inventoryStatusTone,
  type InventoryStatus,
} from '../../../utils/inventory-status';

export interface InventoryItem {
  id: string;
  image: string;
  productName: string;
  variant: string;
  brand: string;
  category: string;
  categoryNames?: string[];
  store: string;
  storeId: number | null;
  sku: string;
  mrp: number;
  sellingPrice: number;
  stock: number;
  lowStockThreshold: number;
  status: InventoryStatus;
  isAvailable: boolean;
  updatedAt: string | null;
  rawStoreProductId?: number;
}

type EditorMode = 'add' | 'set';

@Component({
  selector: 'app-inventory-list',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminPaginatorComponent, IndianCompactPipe, StockReasonComponent],
  templateUrl: './inventory-list.component.html',
  styleUrls: ['./inventory-list.component.scss'],
})
export class InventoryListComponent implements OnInit {
  public storeProductService = inject(StoreProductService);
  public snackbar = inject(SnackbarService);
  public router = inject(Router);
  public authService = inject(AuthService);

  searchTerm = signal('');
  editorReason = signal('');
  selectedStore = signal('All');
  selectedBrand = signal('All');
  selectedCategory = signal('All');
  selectedStatus = signal('All');
  showLowStockOnly = signal(false);

  selectedItem = signal<InventoryItem | null>(null);
  editorOpen = signal(false);
  editorImageFailed = signal(false);
  editorMode = signal<EditorMode>('set');
  editorQuantity = signal(0);
  editorThreshold = signal(5);
  editorAvailable = signal(true);
  editorSaving = signal(false);

  inventory = signal<InventoryItem[]>([]);
  isLoading = signal<boolean>(false);
  currentPage = signal(1);
  pageSize = signal(10);
  private loadSequence = 0;

  ngOnInit(): void {
    this.loadInventory();
  }

  loadInventory() {
    const sequence = ++this.loadSequence;
    this.isLoading.set(true);
    const role = this.authService.role();
    const assignedStoreId = this.authService.storeId();
    const filters =
      role === 'store_manager' && assignedStoreId
        ? { storeId: Number(assignedStoreId) }
        : undefined;

    this.storeProductService.getStoreProducts(filters).subscribe({
      next: (response) => {
        if (sequence !== this.loadSequence) return;
        const data = response.data as any[];
        const mapped: InventoryItem[] = data.map((row: any) => {
          const variant = row.variant;
          const product = variant?.product;

          let imageUrl =
            'https://ui-avatars.com/api/?name=Product&background=random';
          if (product?.images && product.images.length > 0) {
            const primaryImg =
              product.images.find((img: any) => img.is_primary) ||
              product.images[0];
            if (primaryImg?.files?.bucket && primaryImg?.files?.path) {
              imageUrl = getPublicUrl(
                primaryImg.files.bucket,
                primaryImg.files.path
              );
            }
          }

          const stock = typeof row.stock === 'number' ? row.stock : 0;
          const isAvail = row.is_available === true;
          const threshold =
            typeof row.low_stock_threshold === 'number'
              ? row.low_stock_threshold
              : 5;

          return {
            id: `INV-${row.id}`,
            rawStoreProductId: row.id,
            image: imageUrl,
            productName: product?.name || 'Unknown Product',
            variant: getVariantDisplayQuantity(variant) || 'Unit',
            brand: product?.brand?.name || 'Generic',
            category:
              product?.categories?.[0]?.categories?.name || 'Uncategorized',
            categoryNames: product?.categories?.length
              ? [...new Set<string>(product.categories.map((entry: any) => entry.categories?.name).filter(Boolean))]
              : ['Uncategorized'],
            store: row.store?.name || `Store #${row.store_id || 'Unknown'}`,
            storeId: row.store_id ?? null,
            sku: variant?.sku || 'N/A',
            mrp: variant?.mrp ?? 0,
            sellingPrice: row.price ?? variant?.price ?? 0,
            stock,
            lowStockThreshold: threshold,
            status: deriveInventoryStatus(stock, isAvail, threshold),
            isAvailable: isAvail,
            updatedAt: row.updated_at ?? row.created_at ?? null,
          };
        });
        this.inventory.set(mapped);
        this.currentPage.set(1);
        this.isLoading.set(false);
      },
      error: (err) => {
        if (sequence !== this.loadSequence) return;
        console.error('Failed to load inventory', err);
        this.snackbar.error('Could not load inventory from database.');
        this.isLoading.set(false);
      },
    });
  }

  totalItems = computed(() => this.inventory().length);
  inStockCount = computed(
    () => this.inventory().filter((i) => i.status === 'IN STOCK').length
  );
  lowStock = computed(
    () => this.inventory().filter((i) => i.status === 'LOW STOCK').length
  );
  outOfStock = computed(
    () => this.inventory().filter((i) => i.status === 'OUT OF STOCK').length
  );
  unavailableCount = computed(
    () => this.inventory().filter((i) => i.status === 'UNAVAILABLE').length
  );

  get stores() {
    return ['All', ...new Set(this.inventory().map((i) => i.store))];
  }
  get brands() {
    return ['All', ...new Set(this.inventory().map((i) => i.brand))];
  }
  get categories() {
    return ['All', ...new Set(this.inventory().flatMap((i) => i.categoryNames ?? [i.category]))];
  }

  filteredInventory = computed(() => {
    let items = this.inventory();

    if (this.selectedStore() !== 'All') {
      items = items.filter((i) => i.store === this.selectedStore());
    }
    if (this.selectedBrand() !== 'All') {
      items = items.filter((i) => i.brand === this.selectedBrand());
    }
    if (this.selectedCategory() !== 'All') {
      items = items.filter((i) => (i.categoryNames ?? [i.category]).includes(this.selectedCategory()));
    }
    if (this.selectedStatus() !== 'All') {
      items = items.filter((i) => i.status === this.selectedStatus());
    }
    if (this.showLowStockOnly()) {
      items = items.filter((i) => i.status === 'LOW STOCK');
    }

    const s = this.searchTerm().toLowerCase().trim();
    if (s) {
      items = items.filter(
        (i) =>
          i.productName.toLowerCase().includes(s) ||
          i.sku.toLowerCase().includes(s) ||
          i.brand.toLowerCase().includes(s) ||
          i.store.toLowerCase().includes(s)
      );
    }

    return items;
  });

  paginatedInventory = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filteredInventory().slice(start, start + this.pageSize());
  });

  resetPage(): void {
    this.currentPage.set(1);
  }

  changePageSize(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  selectItem(item: InventoryItem) {
    this.selectedItem.set(item);
    this.editorThreshold.set(item.lowStockThreshold);
    this.editorAvailable.set(item.isAvailable);
    this.editorQuantity.set(0);
    this.editorMode.set('set');
  }

  editItem(item: InventoryItem, mode: EditorMode = 'set'): void {
    this.selectItem(item);
    this.openEditor(mode);
  }

  clearSelection() {
    this.selectedItem.set(null);
    this.editorOpen.set(false);
  }

  statusBadgeClass(status: InventoryStatus): string {
    return inventoryStatusTone(status);
  }

  openEditor(mode: EditorMode) {
    const item = this.selectedItem();
    if (!item) return;
    this.editorImageFailed.set(false);
    this.editorMode.set(mode);
    this.editorReason.set('');
    this.editorQuantity.set(mode === 'set' ? item.stock : 0);
    this.editorThreshold.set(item.lowStockThreshold);
    this.editorAvailable.set(item.isAvailable);
    this.editorOpen.set(true);
  }

  resultingStock(): number {
    const item = this.selectedItem();
    if (!item) return 0;
    const qty = Number(this.editorQuantity()) || 0;
    return this.editorMode() === 'set' ? qty : item.stock + qty;
  }

  async saveEditor() {
    const item = this.selectedItem();
    if (!item?.rawStoreProductId) return;
    const reason = this.editorReason();
    if (!reason || reason.trim().length < 5) { this.snackbar.error('A reason of at least five characters is required'); return; }

    const qty = Number(this.editorQuantity());
    const threshold = Number(this.editorThreshold());
    if (!Number.isFinite(qty) || qty < 0) {
      this.snackbar.error('Enter a valid non-negative quantity');
      return;
    }
    if (!Number.isFinite(threshold) || threshold < 0) {
      this.snackbar.error('Enter a valid non-negative threshold');
      return;
    }

    this.editorSaving.set(true);
    try {
      await this.storeProductService
        .mutateStoreProduct({
          storeProductId: item.rawStoreProductId,
          reason,
          setStock: this.editorMode() === 'set' ? Math.floor(qty) : undefined,
          addStock: this.editorMode() === 'add' ? Math.floor(qty) : undefined,
          isAvailable: this.editorAvailable(),
          lowStockThreshold: Math.floor(threshold),
        })
        .toPromise();
      this.snackbar.success('Inventory updated');
      this.editorOpen.set(false);
      this.loadInventory();
    } catch (e) {
      console.error(e);
      this.snackbar.error('Failed to update inventory');
    } finally {
      this.editorSaving.set(false);
    }
  }

  toNumber(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  onAddStoreProduct() {
    this.router.navigate(['/inventory/store-products/add']);
  }
}
